import { Component, signal, computed, effect, input, inject, untracked, ChangeDetectionStrategy, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { EnvironmentService } from 'src/app/services/environment.service';
import { isTreoEnvironment } from 'src/app/utils/environment.utils';
import { DashboardDateRange } from 'src/app/models/dashboard-date-range.model';

/**
 * OpenSearch criteria for text-based search
 */
/**
 * Search component with two distinct functionalities:
 * 1. Dashboard Date Filtering: Date range picker that auto-fetches dashboard data on change
 * 2. OpenSearch Text Search: Free text search independent of dates
 */
@Component({
	selector: 'app-search',
	imports: [FormsModule, InputTextModule, ButtonModule, DatePickerModule, TooltipModule],
	templateUrl: './search.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './search.component.scss',
})
export class SearchComponent {
	private environmentService = inject(EnvironmentService);

	// sessionStorage key base for the date-range selection; survives navigation and page
	// refresh, clears when the browser tab closes (same approach as the dashboard filter
	// state). Namespaced per environment so the range is specific to the environment it was
	// set on and doesn't carry over to another.
	private static readonly SESSION_KEY_DATE = 'analyzer_dashboard_date_range';

	// Current environment as a signal, used to namespace the persistence key
	private currentEnvironment = toSignal(this.environmentService.environment$, {
		initialValue: this.environmentService.getCurrentEnvironment(),
	});

	// Parsed persisted state for the current environment, used to seed the signals below
	private stored = this.loadStoredState();

	// Dashboard date range filtering (auto-triggers on change). Restored from sessionStorage
	// if present, otherwise defaults to the last 2 weeks.
	startDate = signal<Date | null>(this.parseStoredDate(this.stored?.startDate) ?? this.getDateDaysAgo(14));

	endDate = signal<Date | null>(this.parseStoredDate(this.stored?.endDate) ?? new Date());

	// Time selection mode toggle
	showTimeSelection = signal<boolean>(this.stored?.showTimeSelection ?? false);

	// Custom time input fields (empty by default, user types values)
	startHour = signal<string>(this.stored?.startHour ?? '');

	startMinute = signal<string>(this.stored?.startMinute ?? '');

	endHour = signal<string>(this.stored?.endHour ?? '');

	endMinute = signal<string>(this.stored?.endMinute ?? '');

	// Currently selected quick-filter preset ('1d' | '1w' | '2w' | '1m' | '3m'), or null when
	// the range was set manually via the datepicker. Drives the highlighted-button styling AND whether a
	// restored range auto-loads: it is persisted (below) so that after a refresh / env-switch / back-nav a
	// preset range still loads immediately instead of falling back to the "press Apply" deferred gate.
	activePreset = signal<'1d' | '1w' | '2w' | '1m' | '3m' | null>(this.normalizePreset(this.stored?.activePreset));

	// Input: Environment availability - search is disabled when no environment selected
	hasEnvironment = input<boolean>(false);

	showMonthFilter = computed(() => isTreoEnvironment(this.currentEnvironment()));

	/**
	 * Emits whenever date range changes - used for dashboard data fetching
	 */
	readonly dateRangeChange = output<DashboardDateRange>();

	// Key of the last emitted (start, end, preset), so the auto-emit effect can skip re-emitting when only
	// an unrelated dependency changed (e.g. toggling time selection) without changing the effective window.
	private lastEmittedRangeKey = '';

	constructor() {
		// Auto-emit date range changes for dashboard
		effect(() => {
			let start = this.startDate();
			let end = this.endDate();

			// If time selection is NOT enabled, normalize dates to full day range:
			// - Start date: beginning of day (00:00:00.000)
			// - End date: end of day (23:59:59.999)
			// This ensures same-day queries include all items from that day
			if (!this.showTimeSelection()) {
				if (start) {
					const adjustedStart = new Date(start);
					adjustedStart.setHours(0, 0, 0, 0);
					start = adjustedStart;
				}

				if (end) {
					const adjustedEnd = new Date(end);
					adjustedEnd.setHours(23, 59, 59, 999);
					end = adjustedEnd;
				}
			}

			// Only emit if both dates are set (required for the query)
			// Note: Allow start > end (user might be entering custom times)
			if (start && end) {
				const key = `${start.getTime()}|${end.getTime()}|${this.activePreset() ?? ''}`;
				if (key !== this.lastEmittedRangeKey) {
					this.lastEmittedRangeKey = key;
					this.dateRangeChange.emit({
						startDate: start,
						endDate: end,
						// activePreset is set by the preset buttons and cleared on manual date-picker edits, so it
						// reflects whether THIS range came from a quick-filter.
						preset: this.activePreset(),
					});
				}
			}
		});

		// When the environment changes, reload the date range from that environment's storage
		// so the selection is environment-specific. untracked() keeps the writes from also
		// depending on the signals being reloaded.
		effect(() => {
			this.currentEnvironment();
			untracked(() => {
				const s = this.loadStoredState();
				this.startDate.set(this.parseStoredDate(s?.startDate) ?? this.getDateDaysAgo(14));
				this.endDate.set(this.parseStoredDate(s?.endDate) ?? new Date());
				this.showTimeSelection.set(s?.showTimeSelection ?? false);
				this.startHour.set(s?.startHour ?? '');
				this.startMinute.set(s?.startMinute ?? '');
				this.endHour.set(s?.endHour ?? '');
				this.endMinute.set(s?.endMinute ?? '');
				this.activePreset.set(this.normalizePreset(s?.activePreset));
			});
		});

		// Persist the date-range selection so it survives navigation and page refresh.
		// Reacts to the date/time signals only; the environment is read untracked so switching
		// environments doesn't write the outgoing environment's range into the incoming one's key
		// (the reload effect above updates the signals first, which then triggers this save).
		effect(() => {
			const start = this.startDate();
			const end = this.endDate();
			const showTime = this.showTimeSelection();
			const startHour = this.startHour();
			const startMinute = this.startMinute();
			const endHour = this.endHour();
			const endMinute = this.endMinute();
			const activePreset = this.activePreset();
			const env = untracked(() => this.currentEnvironment());
			this.saveStoredState(
				{
					startDate: start ? start.toISOString() : null,
					endDate: end ? end.toISOString() : null,
					showTimeSelection: showTime,
					startHour,
					startMinute,
					endHour,
					endMinute,
					// Persisted so a preset range still auto-loads after refresh / env-switch / back-nav.
					activePreset,
					savedOn: this.todayKey(),
				},
				env,
			);
		});
	}

	/**
	 * Reads the persisted date-range state from sessionStorage.
	 * Returns null on miss or parse error.
	 *
	 * If the state was saved on an earlier day, the persisted end date is dropped
	 * (returned as null) so it falls back to today. This way, when a user starts a
	 * new day they see the latest data instead of yesterday's stale end date.
	 */
	private loadStoredState(): {
		startDate: string | null;
		endDate: string | null;
		showTimeSelection: boolean;
		startHour: string;
		startMinute: string;
		endHour: string;
		endMinute: string;
		activePreset?: string | null;
	} | null {
		try {
			const raw = sessionStorage.getItem(this.envKey());
			if (!raw) return null;
			const state = JSON.parse(raw);
			if (state?.savedOn !== this.todayKey()) {
				// Stale (saved on a previous day): reset the end date to today. The persisted preset no
				// longer describes the (now shifted) window, so drop it — the restored range is treated as
				// manual and subject to the normal auto-load/defer size rule.
				state.endDate = null;
				state.activePreset = null;
			}
			return state;
		} catch {
			return null;
		}
	}

	/**
	 * Local calendar day key (YYYY-MM-DD) used to detect a day rollover between
	 * a saved date range and now.
	 */
	private todayKey(): string {
		const now = new Date();
		const year = now.getFullYear();
		const month = `${now.getMonth() + 1}`.padStart(2, '0');
		const day = `${now.getDate()}`.padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	/**
	 * Writes the date-range state to the given environment's sessionStorage key,
	 * ignoring storage errors.
	 * @param state
	 * @param env
	 */
	private saveStoredState(state: unknown, env: string | null = this.currentEnvironment()): void {
		try {
			sessionStorage.setItem(this.envKey(env), JSON.stringify(state));
		} catch {
			// Ignore storage errors
		}
	}

	/**
	 * Builds the environment-namespaced sessionStorage key for the date range,
	 * e.g. "analyzer_dashboard_date_range_treo".
	 * @param env
	 */
	private envKey(env: string | null = this.currentEnvironment()): string {
		return `${SearchComponent.SESSION_KEY_DATE}_${env || 'none'}`;
	}

	/**
	 * Parses a stored ISO date string into a Date, returning null for missing/invalid values.
	 * @param iso
	 */
	private parseStoredDate(iso: string | null | undefined): Date | null {
		if (!iso) return null;
		const date = new Date(iso);
		return isNaN(date.getTime()) ? null : date;
	}

	/**
	 * Validates a persisted preset string against the known presets, returning null for anything else
	 * (missing, legacy value, or a preset that doesn't apply to this env — e.g. a stored '3m' when only
	 * TREO's up-to-2w buttons are shown; an unknown preset just falls back to the manual-range rule).
	 * @param value
	 */
	private normalizePreset(value: string | null | undefined): '1d' | '1w' | '2w' | '1m' | '3m' | null {
		return value === '1d' || value === '1w' || value === '2w' || value === '1m' || value === '3m' ? value : null;
	}

	/**
	 * Gets a date N months ago from today
	 * @param months
	 */
	private getDateMonthsAgo(months: number): Date {
		const date = new Date();
		date.setMonth(date.getMonth() - months);
		return date;
	}

	/**
	 * Gets a date N days ago from today
	 * @param days
	 */
	private getDateDaysAgo(days: number): Date {
		const date = new Date();
		date.setDate(date.getDate() - days);
		return date;
	}

	/**
	 * Sets date range to past 1 day
	 */
	public setOneDayRange() {
		this.startDate.set(this.getDateDaysAgo(1));
		this.endDate.set(new Date());
		this.activePreset.set('1d');
	}

	/**
	 * Sets date range to past 1 week
	 */
	public setOneWeekRange() {
		this.startDate.set(this.getDateDaysAgo(7));
		this.endDate.set(new Date());
		this.activePreset.set('1w');
	}

	/**
	 * Sets date range to past 2 weeks
	 */
	public setTwoWeeksRange() {
		this.startDate.set(this.getDateDaysAgo(14));
		this.endDate.set(new Date());
		this.activePreset.set('2w');
	}

	/**
	 * Sets date range to past 1 month
	 */
	public setOneMonthRange() {
		this.startDate.set(this.getDateMonthsAgo(1));
		this.endDate.set(new Date());
		this.activePreset.set('1m');
	}

	/**
	 * Sets date range to past 3 months
	 */
	public setThreeMonthsRange() {
		this.startDate.set(this.getDateMonthsAgo(3));
		this.endDate.set(new Date());
		this.activePreset.set('3m');
	}

	/**
	 * Clears the active quick-filter highlight. Called when the user picks a date manually,
	 * since the range no longer matches a preset.
	 */
	public clearActivePreset() {
		this.activePreset.set(null);
	}

	/**
	 * Toggles time selection mode (hours and minutes)
	 */
	public toggleTimeSelection() {
		const newValue = !this.showTimeSelection();
		this.showTimeSelection.set(newValue);

		// When disabling time selection, reset dates to midnight and clear time inputs
		if (!newValue) {
			const start = this.startDate();
			const end = this.endDate();

			if (start) {
				start.setHours(0, 0, 0, 0);
				this.startDate.set(new Date(start));
			}

			if (end) {
				end.setHours(0, 0, 0, 0);
				this.endDate.set(new Date(end));
			}

			// Reset time input values to empty
			this.startHour.set('');
			this.startMinute.set('');
			this.endHour.set('');
			this.endMinute.set('');
		}
	}

	/**
	 * Updates start date hour (during typing - no padding)
	 * @param value
	 */
	public onStartHourChange(value: string) {
		// If empty, allow it
		if (value === '') {
			this.startHour.set('');
			return;
		}

		// Limit to 2 digits max - truncate if longer
		const limitedValue = value.slice(0, 2);

		const hour = parseInt(limitedValue, 10);

		if (isNaN(hour) || hour < 0 || hour > 23) {
			// Reset to current value if invalid
			this.startHour.set(this.startHour());
			return;
		}

		// Don't pad during typing - just set the raw value
		this.startHour.set(limitedValue);
	}

	/**
	 * Pads and validates start hour when user leaves the field
	 * @param event
	 */
	public onStartHourBlur(event?: FocusEvent) {
		// Read from input field to get the actual typed value
		const inputElement = event?.target as HTMLInputElement;
		const inputValue = inputElement?.value || this.startHour();

		if (inputValue === '') {
			this.startHour.set('00');
			this.updateStartDateTime();
			return;
		}

		let hour = parseInt(inputValue, 10);

		if (isNaN(hour) || hour < 0) {
			this.startHour.set('00');
			this.updateStartDateTime();
			return;
		}

		// Clamp to valid range (0-23)
		if (hour > 23) {
			hour = 23;
		}

		const paddedValue = String(hour).padStart(2, '0');
		this.startHour.set(paddedValue);
		this.updateStartDateTime();
	}

	/**
	 * Updates start date minute (during typing - no padding)
	 * @param value
	 */
	public onStartMinuteChange(value: string) {
		// If empty, allow it
		if (value === '') {
			this.startMinute.set('');
			return;
		}

		// Limit to 2 digits max - truncate if longer
		const limitedValue = value.slice(0, 2);

		const minute = parseInt(limitedValue, 10);

		if (isNaN(minute) || minute < 0 || minute > 59) {
			// Reset to current value if invalid
			this.startMinute.set(this.startMinute());
			return;
		}

		// Don't pad during typing - just set the raw value
		this.startMinute.set(limitedValue);
	}

	/**
	 * Pads and validates start minute when user leaves the field
	 * @param event
	 */
	public onStartMinuteBlur(event?: FocusEvent) {
		// Read from input field to get the actual typed value
		const inputElement = event?.target as HTMLInputElement;
		const inputValue = inputElement?.value || this.startMinute();

		if (inputValue === '') {
			this.startMinute.set('00');
			this.updateStartDateTime();
			return;
		}

		let minute = parseInt(inputValue, 10);

		if (isNaN(minute) || minute < 0) {
			this.startMinute.set('00');
			this.updateStartDateTime();
			return;
		}

		// Clamp to valid range (0-59)
		if (minute > 59) {
			minute = 59;
		}

		const paddedValue = String(minute).padStart(2, '0');
		this.startMinute.set(paddedValue);
		this.updateStartDateTime();
	}

	/**
	 * Updates end date hour (during typing - no padding)
	 * @param value
	 */
	public onEndHourChange(value: string) {
		// If empty, allow it
		if (value === '') {
			this.endHour.set('');
			return;
		}

		// Limit to 2 digits max - truncate if longer
		const limitedValue = value.slice(0, 2);

		const hour = parseInt(limitedValue, 10);

		if (isNaN(hour) || hour < 0 || hour > 23) {
			// Reset to current value if invalid
			this.endHour.set(this.endHour());
			return;
		}

		// Don't pad during typing - just set the raw value
		this.endHour.set(limitedValue);
	}

	/**
	 * Pads and validates end hour when user leaves the field
	 * @param event
	 */
	public onEndHourBlur(event?: FocusEvent) {
		// Read from input field to get the actual typed value
		const inputElement = event?.target as HTMLInputElement;
		const inputValue = inputElement?.value || this.endHour();

		if (inputValue === '') {
			this.endHour.set('00');
			this.updateEndDateTime();
			return;
		}

		let hour = parseInt(inputValue, 10);

		if (isNaN(hour) || hour < 0) {
			this.endHour.set('00');
			this.updateEndDateTime();
			return;
		}

		// Clamp to valid range (0-23)
		if (hour > 23) {
			hour = 23;
		}

		const paddedValue = String(hour).padStart(2, '0');
		this.endHour.set(paddedValue);
		this.updateEndDateTime();
	}

	/**
	 * Updates end date minute (during typing - no padding)
	 * @param value
	 */
	public onEndMinuteChange(value: string) {
		// If empty, allow it
		if (value === '') {
			this.endMinute.set('');
			return;
		}

		// Limit to 2 digits max - truncate if longer
		const limitedValue = value.slice(0, 2);

		const minute = parseInt(limitedValue, 10);

		if (isNaN(minute) || minute < 0 || minute > 59) {
			// Reset to current value if invalid
			this.endMinute.set(this.endMinute());
			return;
		}

		// Don't pad during typing - just set the raw value
		this.endMinute.set(limitedValue);
	}

	/**
	 * Pads and validates end minute when user leaves the field
	 * @param event
	 */
	public onEndMinuteBlur(event?: FocusEvent) {
		// Read from input field to get the actual typed value
		const inputElement = event?.target as HTMLInputElement;
		const inputValue = inputElement?.value || this.endMinute();

		if (inputValue === '') {
			this.endMinute.set('00');
			this.updateEndDateTime();
			return;
		}

		let minute = parseInt(inputValue, 10);

		if (isNaN(minute) || minute < 0) {
			this.endMinute.set('00');
			this.updateEndDateTime();
			return;
		}

		// Clamp to valid range (0-59)
		if (minute > 59) {
			minute = 59;
		}

		const paddedValue = String(minute).padStart(2, '0');
		this.endMinute.set(paddedValue);
		this.updateEndDateTime();
	}

	/**
	 * Applies custom time inputs to start date
	 */
	private updateStartDateTime() {
		const date = this.startDate();
		if (!date) return;

		const hourValue = this.startHour();
		const minuteValue = this.startMinute();

		const newDate = new Date(date);
		newDate.setHours(hourValue === '' ? 0 : parseInt(hourValue, 10));
		newDate.setMinutes(minuteValue === '' ? 0 : parseInt(minuteValue, 10));
		newDate.setSeconds(0);

		this.startDate.set(newDate);
	}

	/**
	 * Applies custom time inputs to end date
	 */
	private updateEndDateTime() {
		const date = this.endDate();
		if (!date) return;

		const hourValue = this.endHour();
		const minuteValue = this.endMinute();

		const newDate = new Date(date);
		newDate.setHours(hourValue === '' ? 0 : parseInt(hourValue, 10));
		newDate.setMinutes(minuteValue === '' ? 0 : parseInt(minuteValue, 10));
		newDate.setSeconds(0);

		this.endDate.set(newDate);
	}

	// Increment/Decrement methods for start time
	public incrementStartHour() {
		const currentValue = this.startHour();
		const hour = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newHour = hour >= 23 ? 0 : hour + 1;
		this.startHour.set(String(newHour).padStart(2, '0'));
		this.updateStartDateTime();
	}

	public decrementStartHour() {
		const currentValue = this.startHour();
		const hour = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newHour = hour <= 0 ? 23 : hour - 1;
		this.startHour.set(String(newHour).padStart(2, '0'));
		this.updateStartDateTime();
	}

	public incrementStartMinute() {
		const currentValue = this.startMinute();
		const minute = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newMinute = minute >= 59 ? 0 : minute + 1;
		this.startMinute.set(String(newMinute).padStart(2, '0'));
		this.updateStartDateTime();
	}

	public decrementStartMinute() {
		const currentValue = this.startMinute();
		const minute = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newMinute = minute <= 0 ? 59 : minute - 1;
		this.startMinute.set(String(newMinute).padStart(2, '0'));
		this.updateStartDateTime();
	}

	// Increment/Decrement methods for end time
	public incrementEndHour() {
		const currentValue = this.endHour();
		const hour = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newHour = hour >= 23 ? 0 : hour + 1;
		this.endHour.set(String(newHour).padStart(2, '0'));
		this.updateEndDateTime();
	}

	public decrementEndHour() {
		const currentValue = this.endHour();
		const hour = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newHour = hour <= 0 ? 23 : hour - 1;
		this.endHour.set(String(newHour).padStart(2, '0'));
		this.updateEndDateTime();
	}

	public incrementEndMinute() {
		const currentValue = this.endMinute();
		const minute = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newMinute = minute >= 59 ? 0 : minute + 1;
		this.endMinute.set(String(newMinute).padStart(2, '0'));
		this.updateEndDateTime();
	}

	public decrementEndMinute() {
		const currentValue = this.endMinute();
		const minute = currentValue === '' ? 0 : parseInt(currentValue, 10);
		const newMinute = minute <= 0 ? 59 : minute - 1;
		this.endMinute.set(String(newMinute).padStart(2, '0'));
		this.updateEndDateTime();
	}

	/**
	 * Prevents typing beyond 2 digits in time input fields
	 * Handles Enter key to trigger blur and search
	 * @param event
	 */
	public onTimeInputKeyDown(event: KeyboardEvent): void {
		const input = event.target as HTMLInputElement;
		const currentValue = input.value;

		// Handle Enter key - trigger blur to finalize value and search
		if (event.key === 'Enter') {
			input.blur();
			return;
		}

		// Allow control keys (backspace, delete, arrow keys, tab, etc.)
		const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
		if (allowedKeys.includes(event.key)) {
			return;
		}

		// Allow Ctrl/Cmd combinations (copy, paste, select all, etc.)
		if (event.ctrlKey || event.metaKey) {
			return;
		}

		// If it's not a number, prevent it
		if (!/^\d$/.test(event.key)) {
			event.preventDefault();
			return;
		}

		// If there's a selection, allow typing (it will replace the selection)
		if (input.selectionStart !== input.selectionEnd) {
			return;
		}

		// If we already have 2 characters, prevent typing more
		if (currentValue.length >= 2) {
			event.preventDefault();
			return;
		}
	}
}
